import React, { useEffect, useState, useCallback } from "react";
import client from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { BRAND } from "../mock";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Users, ShieldCheck, GraduationCap, LogOut, Plus, ArrowRight, Check,
  Trash2, UserCog, Clock, Building2, MoreVertical, Wallet, RotateCcw, AlertTriangle,
  X, ShieldAlert, Route, Activity, Inbox,
} from "lucide-react";
import { toast } from "sonner";
import { EnquiriesPanel } from "../components/EnquiriesPanel";
import { LocationsPanel } from "../components/LocationsPanel";
import { tr } from "../i18n/LangContext";

const money = (gbp) => `£${(gbp || 0).toLocaleString()}`;

const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(", ");
  if (d && typeof d === "object") return JSON.stringify(d);
  return d || "Something went wrong";
};

const RoleBadge = ({ role }) => {
  const map = {
    super_admin: ["Super Admin", "border-brand-red/40 bg-brand-red/10 text-brand-red"],
    admin: ["Success Manager", "border-brand-green/40 bg-brand-green/10 text-brand-green"],
    student: ["Student", "border-white/15 bg-white/5 text-muted-foreground"],
  };
  const [label, cls] = map[role] || map.student;
  return <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${cls}`}>{label}</span>;
};

export default function AdminConsole() {
  const { user, isSuper, logout, actAs } = useAuth();
  const [students, setStudents] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [overview, setOverview] = useState(null);
  const [tab, setTab] = useState("students");
  const [enqNew, setEnqNew] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ firstName: "", lastName: "", agency: "", email: "" });
  const [saving, setSaving] = useState(false);
  const [addSMOpen, setAddSMOpen] = useState(false);
  const [smForm, setSmForm] = useState({ firstName: "", lastName: "", email: "" });
  const [savingSM, setSavingSM] = useState(false);
  const [filterSM, setFilterSM] = useState("all");
  const [delTarget, setDelTarget] = useState(null);
  const [delText, setDelText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [reassign, setReassign] = useState(null); // { student, toId, toName, fromId, support }
  const [reassignText, setReassignText] = useState("");
  const [mgrFor, setMgrFor] = useState(null);      // student whose managers we're editing
  const [mgrLead, setMgrLead] = useState(null);
  const [mgrSupport, setMgrSupport] = useState([]);
  const undoTimers = React.useRef({});

  const load = useCallback(async () => {
    try {
      const [s, a, o] = await Promise.all([
        client.get("/admin/users?role=student"),
        client.get("/admin/admins"),
        client.get("/admin/overview"),
      ]);
      setStudents(s.data); setAdmins(a.data); setOverview(o.data);
    } catch (e) { toast.error(tr("Couldn't load console"), { description: errText(e) }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const adminName = (id) => admins.find((a) => a.user_id === id)?.name || "Unassigned";

  const openWorkspace = (s) => {
    actAs(s);
    window.location.assign("/dashboard");
  };
  const openMyCrm = () => {
    actAs({ user_id: user.user_id, name: "My workspace", self: true });
    window.location.assign("/dashboard");
  };
  const approve = async (s) => { await client.post(`/admin/users/${s.user_id}/approve`); toast.success(`${s.name} approved`); load(); };
  const decline = async (s) => {
    try { await client.post(`/admin/users/${s.user_id}/decline`); toast.success(`${s.name} declined`, { description: "They'll be told to reach out to their success manager." }); load(); }
    catch (e) { toast.error(tr("Couldn't decline"), { description: errText(e) }); }
  };

  const doAssign = async (studentId, leadId, supportIds = []) => {
    const res = await client.put(`/admin/users/${studentId}/managers`, { lead_id: leadId, support_ids: supportIds });
    setStudents((prev) => prev.map((x) => (x.user_id === studentId ? res.data : x)));
  };

  // Managers dialog: one lead plus any number of support managers.
  const openManagers = (s) => {
    setMgrFor(s);
    setMgrLead(s.assigned_admin_id || null);
    setMgrSupport(s.support_admin_ids || []);
  };
  const toggleSupport = (id) => setMgrSupport((prev) =>
    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const saveManagers = async () => {
    const s = mgrFor;
    const support = mgrSupport.filter((id) => id !== mgrLead);
    const leadChanged = (s.assigned_admin_id || null) !== (mgrLead || null);
    setMgrFor(null);
    // Changing the lead away from someone still needs the typed confirmation.
    if (leadChanged && s.assigned_admin_id) {
      setReassign({ student: s, toId: mgrLead, toName: mgrLead ? adminName(mgrLead) : "no lead",
                    fromId: s.assigned_admin_id, support });
      setReassignText("");
      return;
    }
    try {
      await doAssign(s.user_id, mgrLead, support);
      toast.success(tr("Managers updated"), { description: mgrLead ? `Lead: ${adminName(mgrLead)}` : "No lead set" });
    } catch (e) { toast.error(tr("Couldn't update managers"), { description: errText(e) }); load(); }
  };
  // Assign flow: unassigned -> assign directly. Already assigned to someone else -> confirm "REASSIGN".
  const confirmReassign = async () => {
    if (!reassign) return;
    const { student, toId, fromId, toName, support } = reassign;
    const sid = student.user_id;
    const prevSupport = student.support_admin_ids || [];
    setReassign(null); setReassignText("");
    try {
      await doAssign(sid, toId, support || []);
      const key = `reassign_${sid}`;
      const timer = setTimeout(() => { delete undoTimers.current[key]; }, 15000);
      undoTimers.current[key] = timer;
      toast.success(`Reassigned to ${toName}`, {
        description: "Notes and CRM data are untouched.",
        duration: 15000,
        action: {
          label: "Undo",
          onClick: async () => {
            clearTimeout(undoTimers.current[key]); delete undoTimers.current[key];
            try { await doAssign(sid, fromId, prevSupport); toast.success(tr("Reassignment undone")); } catch (e) { toast.error(tr("Undo failed")); load(); }
          },
        },
      });
    } catch (e) { toast.error(tr("Couldn't reassign"), { description: errText(e) }); load(); }
  };
  const del = (u) => { setDelTarget(u); setDelText(""); };
  const confirmDelete = async () => {
    if (!delTarget) return;
    setDeleting(true);
    try {
      await client.delete(`/admin/users/${delTarget.user_id}`);
      toast.success(`${delTarget.name} deleted`, { description: "Recoverable for 24 hours from the Recently deleted list." });
      setDelTarget(null); setDelText(""); load();
    } catch (e) { toast.error(tr("Couldn't delete"), { description: errText(e) }); }
    finally { setDeleting(false); }
  };
  const restore = async (u) => {
    try { await client.post(`/admin/users/${u.user_id}/restore`); toast.success(`${u.name} restored`); load(); }
    catch (e) { toast.error(tr("Couldn't restore"), { description: errText(e) }); }
  };
  const setRole = async (u, role) => {
    try { await client.patch(`/admin/users/${u.user_id}/role`, { role }); toast.success(`Now ${role === "admin" ? "a success manager" : "a student"}`); load(); }
    catch (e) { toast.error(tr("Couldn't change role"), { description: errText(e) }); }
  };
  const addStudent = async () => {
    const first = addForm.firstName.trim();
    const email = addForm.email.trim().toLowerCase();
    if (!first) { toast.error(tr("First name is required")); return; }
    if (!email.includes("@")) { toast.error(tr("Enter a valid email (must contain @)")); return; }
    const name = [first, addForm.lastName.trim()].filter(Boolean).join(" ");
    setSaving(true);
    try {
      await client.post("/admin/students", { email, name, agency: addForm.agency.trim() || undefined });
      toast.success(tr("Student added"));
      setAddOpen(false); setAddForm({ firstName: "", lastName: "", agency: "", email: "" }); load();
    } catch (e) { toast.error(tr("Couldn't add student"), { description: errText(e) }); }
    finally { setSaving(false); }
  };
  const addSM = async () => {
    const first = smForm.firstName.trim();
    const email = smForm.email.trim().toLowerCase();
    if (!first) { toast.error(tr("First name is required")); return; }
    if (!email.includes("@")) { toast.error(tr("Enter a valid email (must contain @)")); return; }
    const name = [first, smForm.lastName.trim()].filter(Boolean).join(" ");
    setSavingSM(true);
    try {
      await client.post("/admin/admins", { email, name });
      toast.success(tr("Success manager added"));
      setAddSMOpen(false); setSmForm({ firstName: "", lastName: "", email: "" }); load();
    } catch (e) { toast.error(tr("Couldn't add success manager"), { description: errText(e) }); }
    finally { setSavingSM(false); }
  };

  const pending = students.filter((s) => s.status === "pending");
  const declined = students.filter((s) => s.status === "declined");
  const disabled = students.filter((s) => s.status === "disabled");
  const active = students.filter((s) => !["pending", "declined", "disabled"].includes(s.status));
  const successManagers = admins.filter((a) => a.role === "admin");
  const assignable = admins; // supers (Jamal/Ousama) + success managers can all be assigned/filtered
  const managerIds = (s) => [s.assigned_admin_id, ...(s.support_admin_ids || [])].filter(Boolean);
  const shown = filterSM === "all"
    ? active
    : filterSM === "none"
      ? active.filter((s) => managerIds(s).length === 0)
      : active.filter((s) => managerIds(s).includes(filterSM));

  const Stat = ({ icon: Icon, label, value }) => (
    <div className="rounded-2xl border border-white/[0.06] bg-card/40 p-5">
      <div className="flex items-center gap-2 text-muted-foreground"><Icon className="h-4 w-4" /><span className="text-xs uppercase tracking-wide">{label}</span></div>
      <p className="mt-2 font-display text-3xl font-semibold text-white">{value}</p>
    </div>
  );

  const StudentCard = ({ s }) => (
    <div className="rounded-2xl border border-white/[0.06] bg-card/40 p-4 transition-colors hover:border-white/15" data-testid="student-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <img src={s.avatar} alt="" className="h-11 w-11 rounded-xl object-cover ring-1 ring-white/10" />
          <div className="min-w-0">
            <p className="truncate font-medium text-white">{s.name}</p>
            <p className="truncate text-xs text-muted-foreground">{s.email}</p>
            {s.agency && <p className="truncate text-[11px] text-muted-foreground/80">{s.agency}</p>}
          </div>
        </div>
        {isSuper && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="text-muted-foreground hover:text-white" data-testid="student-menu"><MoreVertical className="h-4 w-4" /></button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="glass-strong border-white/10">
              <DropdownMenuItem onClick={() => setRole(s, "admin")} className="gap-2 focus:bg-white/5"><UserCog className="h-4 w-4" /> {tr("Make success manager")}</DropdownMenuItem>
              <DropdownMenuItem onClick={() => del(s)} className="gap-2 text-brand-red focus:bg-brand-red/10 focus:text-brand-red"><Trash2 className="h-4 w-4" /> {tr("Delete student")}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2 text-center">
        <div className="rounded-xl border border-white/8 bg-white/[0.02] p-2">
          <p className="font-display text-lg font-semibold text-white">{s.property_count}</p>
          <p className="text-[10px] uppercase text-muted-foreground">{tr("Properties")}</p>
        </div>
        <div className="rounded-xl border border-white/8 bg-white/[0.02] p-2">
          <p className="font-display text-lg font-semibold text-white">{money(s.pipeline_gbp)}</p>
          <p className="text-[10px] uppercase text-muted-foreground">{tr("Pipeline")}</p>
        </div>
      </div>

      <div className="mt-3 text-xs text-muted-foreground" data-testid="student-managers">
        {managerIds(s).length === 0 ? (
          <span className="text-muted-foreground">{tr("No success manager assigned")}</span>
        ) : (
          <div className="flex flex-wrap items-center gap-1.5">
            {(s.managers || []).map((m) => (
              <span key={m.user_id} data-testid="manager-chip"
                className={`rounded-md border px-1.5 py-0.5 text-[11px] ${m.lead
                  ? "border-brand-green/30 bg-brand-green/10 text-brand-green"
                  : "border-white/12 bg-white/[0.03] text-slate-300"}`}>
                {m.name}{m.lead && " · lead"}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="mt-3 flex gap-2">
        {s.status === "pending" || s.status === "declined" ? (<>
          <Button size="sm" onClick={() => approve(s)} className="flex-1 bg-brand-green hover:bg-brand-green/90 text-white" data-testid="approve-btn"><Check className="h-4 w-4" /> {tr("Approve")}</Button>
          {s.status === "pending" && (
            <Button size="sm" variant="outline" onClick={() => decline(s)} data-testid="decline-btn" className="border-brand-red/30 bg-brand-red/[0.06] text-brand-red hover:bg-brand-red/15"><X className="h-4 w-4" /> {tr("Decline")}</Button>
          )}
        </>) : (
          <Button size="sm" onClick={() => openWorkspace(s)} className="flex-1 bg-brand-red hover:bg-brand-redDark text-white" data-testid="open-workspace-btn">{tr("Open workspace")} <ArrowRight className="h-4 w-4" /></Button>
        )}
        <Button size="sm" variant="outline" onClick={() => openManagers(s)} data-testid="managers-btn"
          className="border-white/12 bg-white/[0.03] text-white hover:bg-white/[0.07]"><UserCog className="h-4 w-4" /> {tr("Managers")}</Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-white/[0.06] bg-background/80 px-4 py-3 backdrop-blur-xl sm:px-6">
        <img src={BRAND.logo} alt="Moroccan Brothers" className="h-14 w-auto max-w-[200px] object-contain" />
        <div className="border-l border-white/10 pl-3">
          <h1 className="font-display text-lg font-semibold text-white">{tr("Mentorship Console")}</h1>
          <p className="text-xs text-muted-foreground">{isSuper ? tr("Oversee every student's CRM") : tr("Your assigned students")} · {user?.name}</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <RoleBadge role={user?.role} />
          <Button onClick={() => window.location.assign("/journey")} className="h-10 border border-white/12 bg-white/[0.03] text-white hover:bg-white/[0.07]" data-testid="journey-btn"><Route className="h-4 w-4" /> {tr("Student Journey")}</Button>
          <Button onClick={() => window.location.assign("/activity")} className="h-10 border border-white/12 bg-white/[0.03] text-white hover:bg-white/[0.07]" data-testid="activity-btn"><Activity className="h-4 w-4" /> {tr("Activity")}</Button>
          {isSuper && <Button onClick={() => window.location.assign("/pnl")} className="h-10 bg-brand-green hover:bg-brand-green/90 text-white" data-testid="pnl-btn"><Wallet className="h-4 w-4" /> P&amp;L</Button>}
          <Button onClick={openMyCrm} className="h-10 border border-white/12 bg-white/[0.03] text-white hover:bg-white/[0.07]" data-testid="my-crm-btn"><Building2 className="h-4 w-4" /> {tr("My CRM")}</Button>
          <Button variant="outline" onClick={logout} className="h-10 border-white/12 bg-white/[0.03] text-white hover:bg-white/[0.07]"><LogOut className="h-4 w-4" /> {tr("Sign out")}</Button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat icon={GraduationCap} label={tr("Students")} value={overview?.students ?? "—"} />
          <Stat icon={Clock} label={tr("Pending")} value={overview?.pending ?? "—"} />
          <Stat icon={ShieldCheck} label={tr("Success Managers")} value={overview?.admins ?? "—"} />
          <div className="rounded-2xl border border-brand-red/20 bg-brand-red/[0.04] p-5 flex flex-col justify-center">
            <Button onClick={() => setAddOpen(true)} className="bg-brand-red hover:bg-brand-redDark text-white" data-testid="add-student-btn"><Plus className="h-4 w-4" /> {tr("Add student")}</Button>
          </div>
        </div>

        {pending.length > 0 && (
          <div className="mt-8 rounded-2xl border border-brand-green/25 bg-brand-green/[0.04] p-5" data-testid="approval-needed-section">
            <h2 className="mb-1 flex items-center gap-2 font-display text-lg font-semibold text-white"><Clock className="h-4 w-4 text-brand-green" /> Approval needed ({pending.length})</h2>
            <p className="mb-4 text-xs text-muted-foreground">{tr("These people signed up themselves. Approving gives them their own CRM — then set their success managers.")}</p>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{pending.map((s) => <StudentCard key={s.user_id} s={s} />)}</div>
          </div>
        )}

        {declined.length > 0 && (
          <div className="mt-6" data-testid="declined-section">
            <h2 className="mb-3 flex items-center gap-2 font-display text-lg font-semibold text-white"><ShieldAlert className="h-4 w-4 text-brand-red" /> Declined ({declined.length})</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{declined.map((s) => <StudentCard key={s.user_id} s={s} />)}</div>
          </div>
        )}

        <div className="mt-8 flex items-center gap-2">
          <button onClick={() => setTab("students")} className={`rounded-lg px-3 py-1.5 text-sm font-medium ${tab === "students" ? "bg-white/[0.06] text-white" : "text-muted-foreground hover:text-white"}`} data-testid="tab-students">{tr("Students")}</button>
          {isSuper && <button onClick={() => setTab("admins")} className={`rounded-lg px-3 py-1.5 text-sm font-medium ${tab === "admins" ? "bg-white/[0.06] text-white" : "text-muted-foreground hover:text-white"}`} data-testid="tab-admins">{tr("Success Managers")}</button>}
          {isSuper && <button onClick={() => setTab("locations")} className={`rounded-lg px-3 py-1.5 text-sm font-medium ${tab === "locations" ? "bg-white/[0.06] text-white" : "text-muted-foreground hover:text-white"}`} data-testid="tab-locations">{tr("Locations")}</button>}
          <button onClick={() => setTab("enquiries")} className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium ${tab === "enquiries" ? "bg-white/[0.06] text-white" : "text-muted-foreground hover:text-white"}`} data-testid="tab-enquiries">
            <Inbox className="h-3.5 w-3.5" /> {tr("Enquiries")}
            {(enqNew ?? overview?.enquiries_new) > 0 && <span className="rounded-full bg-brand-red px-1.5 py-0.5 text-[10px] font-bold leading-none text-white" data-testid="enquiries-badge">{enqNew ?? overview?.enquiries_new}</span>}
          </button>
        </div>

        {tab === "locations" ? <LocationsPanel /> : tab === "enquiries" ? <EnquiriesPanel onCount={setEnqNew} /> : tab === "students" ? (
          <>
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">{shown.length} student{shown.length === 1 ? "" : "s"}</p>
              {(
                <div className="flex items-center gap-2">
                  <button onClick={() => setFilterSM(filterSM === user?.user_id ? "all" : user?.user_id)}
                    data-testid="my-students-btn"
                    className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${
                      filterSM === user?.user_id
                        ? "border-brand-green/35 bg-brand-green/10 text-brand-green"
                        : "border-white/12 bg-white/[0.03] text-muted-foreground hover:text-white"}`}>
                    {tr("My students")}
                  </button>
                  <span className="text-xs uppercase tracking-widest text-muted-foreground">{tr("Success Manager")}</span>
                  <Select value={filterSM} onValueChange={setFilterSM}>
                    <SelectTrigger className="h-9 w-[210px] border-white/10 bg-white/[0.03] text-white" data-testid="filter-sm"><SelectValue /></SelectTrigger>
                    <SelectContent className="glass-strong border-white/10">
                      <SelectItem value="all">{tr("All success managers")}</SelectItem>
                      <SelectItem value="none">{tr("Unassigned")}</SelectItem>
                      {assignable.map((a) => <SelectItem key={a.user_id} value={a.user_id}>{a.name}{a.role === "super_admin" ? " (Super)" : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            {shown.length ? (
              <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{shown.map((s) => <StudentCard key={s.user_id} s={s} />)}</div>
            ) : (
              <div className="mt-4 rounded-2xl border border-white/[0.06] bg-card/40 p-10 text-center">
                <Users className="mx-auto h-8 w-8 text-muted-foreground" />
                <p className="mt-3 font-display text-lg text-white">{tr("No students here")}</p>
                <p className="mt-1 text-sm text-muted-foreground">{tr("Add a student by email — they'll get their own CRM.")}</p>
              </div>
            )}

            {isSuper && disabled.length > 0 && (
              <div className="mt-10">
                <h2 className="mb-3 flex items-center gap-2 font-display text-lg font-semibold text-white"><Trash2 className="h-4 w-4 text-brand-red" /> Recently deleted ({disabled.length})</h2>
                <p className="mb-3 text-xs text-muted-foreground">{tr("These are removed permanently 24 hours after deletion. Restore anytime before then.")}</p>
                <div className="space-y-2">
                  {disabled.map((s) => (
                    <div key={s.user_id} className="flex items-center justify-between rounded-xl border border-brand-red/20 bg-brand-red/[0.04] p-3" data-testid="deleted-row">
                      <div className="flex items-center gap-3 min-w-0">
                        <img src={s.avatar} alt="" className="h-9 w-9 rounded-lg object-cover opacity-60 ring-1 ring-white/10" />
                        <div className="min-w-0"><p className="truncate text-sm font-medium text-white">{s.name}</p><p className="truncate text-xs text-muted-foreground">{s.email}</p></div>
                      </div>
                      <Button size="sm" onClick={() => restore(s)} className="bg-brand-green hover:bg-brand-green/90 text-white" data-testid="restore-btn"><RotateCcw className="h-4 w-4" /> {tr("Restore")}</Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="mt-4 space-y-2">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-sm text-muted-foreground">{successManagers.length} success manager{successManagers.length === 1 ? "" : "s"}</p>
              <Button size="sm" onClick={() => setAddSMOpen(true)} className="bg-brand-red hover:bg-brand-redDark text-white" data-testid="add-sm-btn"><Plus className="h-4 w-4" /> {tr("Add success manager")}</Button>
            </div>
            {admins.map((a) => (
              <div key={a.user_id} className="flex items-center justify-between rounded-xl border border-white/8 bg-white/[0.02] p-3" data-testid="admin-row">
                <div className="flex items-center gap-3">
                  <img src={a.avatar} alt="" className="h-9 w-9 rounded-lg object-cover ring-1 ring-white/10" />
                  <div><p className="text-sm font-medium text-white">{a.name}</p><p className="text-xs text-muted-foreground">{a.email}</p></div>
                </div>
                <div className="flex items-center gap-2">
                  <RoleBadge role={a.role} />
                  {a.role === "admin" && (
                    <Button size="sm" variant="outline" onClick={() => del(a)} className="border-brand-red/40 bg-brand-red/10 text-brand-red hover:bg-brand-red/20" data-testid="delete-sm-btn"><Trash2 className="h-4 w-4" /></Button>
                  )}
                </div>
              </div>
            ))}
            <p className="pt-2 text-xs text-muted-foreground">{tr("Add a success manager by email, or promote a student from their card menu. Success managers get their own CRM and can see every student, with “My students” showing who they're responsible for; only super admins manage success managers, roles and the P&L.")}</p>
          </div>
        )}
      </main>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md glass-strong border-white/10">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-white">{tr("Add a student")}</DialogTitle>
            <DialogDescription className="text-muted-foreground">{tr("They'll get their own CRM. When they sign in with Google using this email, they're ready to go.")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-1">
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="add-student-first" autoFocus value={addForm.firstName} onChange={(e) => setAddForm({ ...addForm, firstName: e.target.value })} placeholder={tr("First name *")} className="bg-input/60 border-white/10 focus-visible:ring-brand-red" />
              <Input data-testid="add-student-last" value={addForm.lastName} onChange={(e) => setAddForm({ ...addForm, lastName: e.target.value })} placeholder={tr("Last name (optional)")} className="bg-input/60 border-white/10 focus-visible:ring-brand-red" />
            </div>
            <Input data-testid="add-student-agency" value={addForm.agency} onChange={(e) => setAddForm({ ...addForm, agency: e.target.value })} placeholder={tr("Agency (optional)")} className="bg-input/60 border-white/10 focus-visible:ring-brand-red" />
            <Input data-testid="add-student-email" type="email" value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} placeholder={tr("student@email.com")} className="bg-input/60 border-white/10 focus-visible:ring-brand-red" />
            <Button onClick={addStudent} disabled={saving} className="h-11 w-full bg-brand-red hover:bg-brand-redDark text-white" data-testid="add-student-save">{saving ? tr("Saving…") : tr("Add student")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={addSMOpen} onOpenChange={setAddSMOpen}>
        <DialogContent className="max-w-md glass-strong border-white/10">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-white">{tr("Add a success manager")}</DialogTitle>
            <DialogDescription className="text-muted-foreground">{tr("They get their own CRM and manage the students assigned to them. They sign in with Google using this email.")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-1">
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="add-sm-first" autoFocus value={smForm.firstName} onChange={(e) => setSmForm({ ...smForm, firstName: e.target.value })} placeholder={tr("First name *")} className="bg-input/60 border-white/10 focus-visible:ring-brand-red" />
              <Input data-testid="add-sm-last" value={smForm.lastName} onChange={(e) => setSmForm({ ...smForm, lastName: e.target.value })} placeholder={tr("Last name (optional)")} className="bg-input/60 border-white/10 focus-visible:ring-brand-red" />
            </div>
            <Input data-testid="add-sm-email" type="email" value={smForm.email} onChange={(e) => setSmForm({ ...smForm, email: e.target.value })} placeholder={tr("manager@email.com")} className="bg-input/60 border-white/10 focus-visible:ring-brand-red" />
            <Button onClick={addSM} disabled={savingSM} className="h-11 w-full bg-brand-green hover:bg-brand-green/90 text-white" data-testid="add-sm-save">{savingSM ? tr("Saving…") : tr("Add success manager")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!mgrFor} onOpenChange={(o) => { if (!o) setMgrFor(null); }}>
        <DialogContent className="glass-strong border-white/10 sm:max-w-md" data-testid="managers-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display text-xl text-white">
              <UserCog className="h-5 w-5 text-brand-green" /> {mgrFor?.name}'s managers
            </DialogTitle>
            <DialogDescription>
              {tr("Pick a lead, then tick anyone else who looks after them. Everyone can see every student — this is about who's responsible.")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {assignable.length === 0 && <p className="text-sm text-muted-foreground">{tr("No success managers yet.")}</p>}
            {assignable.map((a) => {
              const isLead = mgrLead === a.user_id;
              const isSupport = mgrSupport.includes(a.user_id) && !isLead;
              return (
                <div key={a.user_id} data-testid={`manager-option-${a.user_id}`}
                  className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 transition-colors ${
                    isLead ? "border-brand-green/35 bg-brand-green/[0.07]"
                    : isSupport ? "border-white/15 bg-white/[0.04]" : "border-white/[0.07] bg-white/[0.02]"}`}>
                  <button onClick={() => toggleSupport(a.user_id)} data-testid={`manager-toggle-${a.user_id}`}
                    className="flex flex-1 items-center gap-2 text-left">
                    <span className={`grid h-4 w-4 place-items-center rounded border ${
                      isLead || isSupport ? "border-brand-green bg-brand-green text-white" : "border-white/25"}`}>
                      {(isLead || isSupport) && <Check className="h-3 w-3" />}
                    </span>
                    <span className="text-sm text-white">{a.name}</span>
                    {a.role === "super_admin" && <span className="text-[10px] uppercase text-brand-red">{tr("Super")}</span>}
                  </button>
                  <button onClick={() => setMgrLead(isLead ? null : a.user_id)} data-testid={`manager-lead-${a.user_id}`}
                    className={`rounded-md px-2 py-1 text-[11px] font-semibold uppercase tracking-wide transition-colors ${
                      isLead ? "bg-brand-green/20 text-brand-green" : "text-muted-foreground hover:text-white"}`}>
                    {isLead ? "Lead" : "Make lead"}
                  </button>
                </div>
              );
            })}
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => setMgrFor(null)} className="flex-1 border-white/12 bg-white/[0.03] text-white">{tr("Cancel")}</Button>
            <Button onClick={saveManagers} data-testid="managers-save-btn" className="flex-1 bg-brand-green hover:bg-brand-green/90 text-white">{tr("Save")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reassign} onOpenChange={(o) => { if (!o) { setReassign(null); setReassignText(""); } }}>
        <DialogContent className="max-w-md glass-strong border-brand-green/30">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display text-xl text-white"><UserCog className="h-5 w-5 text-brand-green" /> Reassign {reassign?.student?.name}?</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Move from <span className="text-white">{reassign ? adminName(reassign.fromId) : ""}</span> {tr("to")} <span className="text-white">{reassign?.toName}</span>. All CRM data and notes stay untouched. You'll have 15 seconds to undo.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-1">
            <p className="text-sm text-muted-foreground">{tr("Type")} <span className="font-semibold text-brand-green">{tr("REASSIGN")}</span> {tr("to confirm.")}</p>
            <Input data-testid="reassign-confirm-input" autoFocus value={reassignText} onChange={(e) => setReassignText(e.target.value)} placeholder={tr("REASSIGN")} className="bg-input/60 border-white/10 focus-visible:ring-brand-green" />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setReassign(null); setReassignText(""); }} className="flex-1 border-white/12 bg-white/[0.03] text-white hover:bg-white/[0.07]">{tr("Cancel")}</Button>
              <Button onClick={confirmReassign} disabled={reassignText !== "REASSIGN"} className="flex-1 bg-brand-green hover:bg-brand-green/90 text-white disabled:opacity-40" data-testid="reassign-confirm-btn"><UserCog className="h-4 w-4" /> {tr("Reassign")}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delTarget} onOpenChange={(o) => { if (!o) { setDelTarget(null); setDelText(""); } }}>
        <DialogContent className="max-w-md glass-strong border-brand-red/30">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display text-xl text-white"><AlertTriangle className="h-5 w-5 text-brand-red" /> Delete {delTarget?.name}?</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              This disables the account immediately. It stays recoverable for <span className="text-white">24 hours</span> from the Recently deleted list, then it and all its data are permanently removed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-1">
            <p className="text-sm text-muted-foreground">{tr("Type")} <span className="font-semibold text-brand-red">{tr("DELETE")}</span> {tr("to confirm.")}</p>
            <Input data-testid="delete-confirm-input" autoFocus value={delText} onChange={(e) => setDelText(e.target.value)} placeholder={tr("DELETE")} className="bg-input/60 border-white/10 focus-visible:ring-brand-red" />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setDelTarget(null); setDelText(""); }} className="flex-1 border-white/12 bg-white/[0.03] text-white hover:bg-white/[0.07]">{tr("Cancel")}</Button>
              <Button onClick={confirmDelete} disabled={delText !== "DELETE" || deleting} className="flex-1 bg-brand-red hover:bg-brand-redDark text-white disabled:opacity-40" data-testid="delete-confirm-btn"><Trash2 className="h-4 w-4" /> {deleting ? "Deleting…" : "Delete"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
